import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation,
  inject,
  signal
} from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FileShort, UploadErrorType } from '@simOn/common/models';
import { NotificationService } from '@simOn/common/notification';
import { IMAGE_SIZE_URL_SUFFIX, SimUploadImageComponent } from '@simOn/common/upload-image';
import { UploadApiService } from '@simOn/common/upload-queue/data-access';
import { SimIconModule } from '@simOn/ui/sim-icon';
import { SimSpinnerComponent } from '@simOn/ui/sim-spinner';
import { catchError, finalize, firstValueFrom, of, tap } from 'rxjs';

const TOAST_INFO = {
  uploadError: $localize`:@@TOASTS_UPLOAD_ERROR:Failed to upload!`
};
@Component({
  selector: 'space-new-property-upload-image',
  imports: [
    CommonModule,
    SimUploadImageComponent,
    FlexLayoutModule,
    FormsModule,
    ReactiveFormsModule,
    SimSpinnerComponent,
    SimIconModule
  ],
  templateUrl: './sim-new-property-upload-image.component.html',
  styleUrls: ['./sim-new-property-upload-image.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class SimNewPropertyUploadImageComponent {
  private readonly _uploadApi = inject(UploadApiService);
  private readonly notificationService = inject(NotificationService);
  readonly isLoading = signal(false);
  readonly imageSizeUrlSuffix = IMAGE_SIZE_URL_SUFFIX;

  @HostBinding('class.select-image-on-hover') get imageOnHover(): boolean {
    return this._selectImageOnHover;
  }
  @HostBinding('class.sim-place-image-small') get smallPlacePicture(): boolean {
    return this._smallImage;
  }

  @Input() id!: string;

  @Input()
  get selectImageOnHover(): boolean {
    return this._selectImageOnHover;
  }
  set selectImageOnHover(value: BooleanInput) {
    this._selectImageOnHover = coerceBooleanProperty(value);
  }
  private _selectImageOnHover = false;

  @Input()
  get imageUrl(): string | ArrayBuffer | undefined {
    return this._imageUrl;
  }
  set imageUrl(value: string | ArrayBuffer | undefined) {
    if (!value) return;
    this._imageUrl = value;
  }
  private _imageUrl!: string | ArrayBuffer;
  readonly defaultImageUrl = 'assets/images/House_sketch.svg';

  @Input()
  get uploadDisabled(): boolean {
    return this._uploadDisabled;
  }
  set uploadDisabled(value: BooleanInput) {
    this._uploadDisabled = coerceBooleanProperty(value);
  }
  private _uploadDisabled = false;

  @Input()
  get small(): boolean {
    return this._smallImage;
  }
  set small(value: BooleanInput) {
    this._smallImage = coerceBooleanProperty(value);
  }
  private _smallImage = false;

  @Output() selectedImage = new EventEmitter<FileShort>();
  @Output() localImage = new EventEmitter<string | ArrayBuffer>();

  @ViewChild('upload') private _image!: SimUploadImageComponent;

  get image(): any {
    return this._image?.value;
  }

  clearUploadImage(): void {
    this._image.value = null;
  }

  showError(event: UploadErrorType): void {
    this.notificationService.openErrorSnackBar(event.message);
  }

  uploadFile(file: File) {
    this.isLoading.set(true);

    firstValueFrom(
      this._uploadApi.sendFileToBlob(file, 'GenerateBlobUrlForUserUpload').pipe(
        tap((id) => {
          if (id) {
            this._image.value && this.localImage.emit(this._image.value);
            this.selectedImage.emit({ id, name: file.name, attachMode: 'FromUpload' });
          }
        }),
        catchError((err) => {
          this.notificationService.openErrorSnackBar(TOAST_INFO.uploadError);
          this._image.value = this.defaultImageUrl;
          return of(err);
        }),
        finalize(() => this.isLoading.set(false))
      )
    );
  }

  dropFile(event: DragEvent) {
    event.dataTransfer?.files[0] && this.uploadFile(event.dataTransfer.files[0]);
  }
}
